/**
 * Shape of the public PWA flag envelope returned by
 * `GET /api/v2/public/feature-flags`. Keys mirror
 * `backend/src/quartel/services/pwa/feature_gate.py::PWA_ALL_KEYS`.
 *
 * Values are plain booleans (already AND-gated server-side against the
 * master `pwa.enabled` would NOT happen here — the server returns each
 * flag's raw state, and the client computes the AND when registering SW
 * or rendering UI). The endpoint always returns False for absent rows so
 * the client receives a stable shape.
 */
export interface PwaFlagSnapshot {
  readonly "pwa.enabled": boolean;
  readonly "pwa.install_prompt_enabled": boolean;
  readonly "pwa.notifications.push_enabled": boolean;
  readonly "pwa.notifications.segments_enabled": boolean;
  readonly "pwa.service_worker_enabled": boolean;
  readonly "pwa.token_bonus.claims_enabled": boolean;
}

export const PWA_FLAG_KEYS: readonly (keyof PwaFlagSnapshot)[] = [
  "pwa.enabled",
  "pwa.install_prompt_enabled",
  "pwa.notifications.push_enabled",
  "pwa.notifications.segments_enabled",
  "pwa.service_worker_enabled",
  "pwa.token_bonus.claims_enabled",
] as const;

export const PWA_FLAGS_ALL_OFF: PwaFlagSnapshot = Object.freeze({
  "pwa.enabled": false,
  "pwa.install_prompt_enabled": false,
  "pwa.notifications.push_enabled": false,
  "pwa.notifications.segments_enabled": false,
  "pwa.service_worker_enabled": false,
  "pwa.token_bonus.claims_enabled": false,
});

/**
 * Master + sub-flag AND-gate. Returns true iff `pwa.enabled` is true AND the
 * named sub-flag is true. The master alone is accessed via `"pwa.enabled"`.
 */
export function isPwaFeatureOn(
  flags: PwaFlagSnapshot,
  key: keyof PwaFlagSnapshot,
): boolean {
  if (!flags["pwa.enabled"]) return false;
  if (key === "pwa.enabled") return true;
  return flags[key];
}
